import { useEffect, useState, createContext, useContext, useCallback, useRef, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import "@/App.css";
import { trackPageView, trackTimeSpent, resetPageTimer, trackSignup } from "./utils/analytics";
import { toast } from "sonner";

// Eagerly-loaded pages (entry point + most-trafficked public pages already in
// critical path — avoids extra network round-trip on first paint).
import LandingPage from "./pages/LandingPage";

// Everything else is code-split into its own chunk so admins, uploaders and
// rare routes don't bloat the public first-paint bundle.
const ProductPage = lazy(() => import("./pages/ProductPage"));
const ProductsPage = lazy(() => import("./pages/ProductsPage"));
const UploadPage = lazy(() => import("./pages/UploadPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const CommunityPage = lazy(() => import("./pages/CommunityPage"));
const CommunityDetailPage = lazy(() => import("./pages/CommunityDetailPage"));
const RoutinesPage = lazy(() => import("./pages/RoutinesPage"));
const SharedRoutinesPage = lazy(() => import("./pages/SharedRoutinesPage"));
const RoutineDetailPage = lazy(() => import("./pages/RoutineDetailPage"));
const PublicRoutineDetailPage = lazy(() => import("./pages/PublicRoutineDetailPage"));
const PublicReviewerProfilePage = lazy(() => import("./pages/PublicReviewerProfilePage"));
const AskRoutinePage = lazy(() => import("./pages/AskRoutinePage"));
const AskAFriendPage = lazy(() => import("./pages/AskAFriendPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const ConcernLandingPage = lazy(() => import("./pages/ConcernLandingPage"));
const WeeklyDigestPage = lazy(() => import("./pages/WeeklyDigestPage"));
const IngredientsListPage = lazy(() => import("./pages/IngredientsListPage"));
const IngredientPage = lazy(() => import("./pages/IngredientPage"));

// Components
import { Toaster } from "./components/ui/sonner";
import OnboardingModal from "./components/OnboardingModal";
import AskAFriendNudge from "./components/AskAFriendNudge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Re-export for backward compatibility
export { API };

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider backed by Auth0
const AuthProvider = ({ children }) => {
  const {
    user: auth0User,
    isAuthenticated,
    isLoading: auth0Loading,
    loginWithRedirect,
    loginWithPopup,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0();

  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const interceptorRef = useRef(null);
  const hasTrackedSignup = useRef(false);

  // Set up global axios interceptor to attach Auth0 Bearer token
  useEffect(() => {
    if (interceptorRef.current !== null) {
      axios.interceptors.request.eject(interceptorRef.current);
    }

    interceptorRef.current = axios.interceptors.request.use(async (config) => {
      // Only add token if no Authorization already set
      if (!config.headers.Authorization) {
        // First check for email/password token
        if (window.__zeel_email_token) {
          config.headers.Authorization = `Bearer ${window.__zeel_email_token}`;
        } else if (isAuthenticated) {
          // Fall back to Auth0 SDK token
          try {
            const token = await getAccessTokenSilently();
            config.headers.Authorization = `Bearer ${token}`;
          } catch (e) {
            // Silently fail — public endpoints don't need a token
          }
        }
      }
      return config;
    });

    return () => {
      if (interceptorRef.current !== null) {
        axios.interceptors.request.eject(interceptorRef.current);
      }
    };
  }, [isAuthenticated, getAccessTokenSilently]);

  // Sync Auth0 user with our backend
  const syncUser = useCallback(async () => {
    if (!isAuthenticated || !auth0User) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const token = await getAccessTokenSilently();
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data);

      // Track signup once per session
      if (!hasTrackedSignup.current) {
        hasTrackedSignup.current = true;
        trackSignup();
      }
    } catch (error) {
      console.error("Failed to sync user with backend:", error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, auth0User, getAccessTokenSilently]);

  useEffect(() => {
    if (auth0Loading) return;
    syncUser();
  }, [auth0Loading, syncUser]);

  const login = useCallback(() => {
    // Route to the Zeel-branded /login page (email/password + Google button)
    // instead of the raw Auth0 Universal Login. This keeps UX consistent with
    // the Navbar Sign Up flow. Callers that need the Auth0 popup flow (e.g.
    // Google sign-in) should use `loginWithGoogle` directly.
    navigate("/login");
  }, [navigate]);

  const loginWithGoogle = useCallback(async () => {
    // Google-specific: always use popup with connection hint
    try {
      await loginWithPopup({ authorizationParams: { connection: "google-oauth2" } });
    } catch (e) {
      // If popup blocked/closed, fall back to redirect
      loginWithRedirect({ authorizationParams: { connection: "google-oauth2" } });
    }
  }, [loginWithPopup, loginWithRedirect]);

  // Email/password login via backend proxy to Auth0
  const loginWithEmailFn = useCallback(async (email, password) => {
    const resp = await axios.post(`${API}/auth/email-login`, { email, password });
    const { access_token, user: userData } = resp.data;
    // Store token for the axios interceptor to use
    window.__zeel_email_token = access_token;
    setUser(userData);
    return userData;
  }, []);

  // Email/password signup via backend proxy to Auth0
  const signupWithEmailFn = useCallback(async (email, password, name) => {
    const resp = await axios.post(`${API}/auth/email-signup`, { email, password, name });
    const { access_token, user: userData } = resp.data;
    window.__zeel_email_token = access_token;
    setUser(userData);
    return userData;
  }, []);

  const logout = () => {
    setUser(null);
    window.__zeel_email_token = null;
    auth0Logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const updateSkinType = async (skinType) => {
    try {
      await axios.patch(`${API}/auth/skin-type`, { skin_type: skinType });
      setUser(prev => prev ? { ...prev, skin_type: skinType } : prev);
    } catch (error) {
      console.error("Error updating skin type:", error);
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, loginWithGoogle, loginWithEmail: loginWithEmailFn, signupWithEmail: signupWithEmailFn, logout, checkAuth: syncUser, updateSkinType }}>
      {children}
    </AuthContext.Provider>
  );
};

// Route change tracker
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    resetPageTimer();
    trackPageView(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    window.addEventListener("beforeunload", trackTimeSpent);
    return () => window.removeEventListener("beforeunload", trackTimeSpent);
  }, []);

  return null;
}

// Scroll to top on route change — SPA navigations otherwise preserve the
// previous page's scroll position, which confuses users who click top-nav
// links and land mid-page.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// App Router
function AppRouter() {
  const location = useLocation();
  const { user, setUser } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Check if user needs onboarding or a reminder
  useEffect(() => {
    if (!user) { setShowOnboarding(false); return; }
    axios.get(`${API}/users/profile`)
      .then(r => {
        if (!r.data.onboarding_complete) {
          setShowOnboarding(true);
        } else if (r.data.onboarding_skipped && !r.data.skin_type) {
          toast("Your skin profile is incomplete", {
            description: "Complete it to get personalised routine matches.",
            action: { label: "Complete now", onClick: () => setShowOnboarding(true) },
            duration: 8000,
          });
        }
      })
      .catch(() => {});
  }, [user]);

  const handleOnboardingComplete = (data) => {
    setShowOnboarding(false);
    if (data?.skin_type) {
      setUser(prev => prev ? { ...prev, skin_type: data.skin_type } : prev);
    }
  };

  return (
    <>
      <ScrollToTop />
      <RouteTracker />
      {showOnboarding && <OnboardingModal onComplete={handleOnboardingComplete} />}
      <Suspense fallback={
        <div className="min-h-[40vh] flex items-center justify-center" data-testid="route-suspense-fallback">
          <div className="w-6 h-6 border-2 border-[#4A55A6] border-t-transparent rounded-full animate-spin" />
        </div>
      }>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/products" element={<ProductsPage />} />
          <Route path="/product/:slug" element={<ProductPage />} />
          <Route path="/upload" element={<UploadPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/community" element={<CommunityPage />} />
          <Route path="/community/top-claims" element={<CommunityDetailPage />} />
          <Route path="/community/best-qa" element={<CommunityDetailPage />} />
          <Route path="/community/top-routines" element={<CommunityDetailPage />} />
          <Route path="/community/top-contributors" element={<CommunityDetailPage />} />
          <Route path="/community/weekly-digest" element={<WeeklyDigestPage />} />
          <Route path="/ingredients" element={<IngredientsListPage />} />
          <Route path="/ingredients/:slug" element={<IngredientPage />} />
          <Route path="/routines" element={<SharedRoutinesPage />} />
          <Route path="/my-routines" element={<RoutinesPage />} />
          <Route path="/my-routines/:routineId" element={<RoutineDetailPage />} />
          <Route path="/routines/:concern" element={<ConcernLandingPage />} />
          <Route path="/routines/public/:routineId" element={<PublicRoutineDetailPage />} />
          <Route path="/askafriend" element={<AskAFriendPage />} />
          <Route path="/ask/:requestId" element={<AskRoutinePage />} />
          <Route path="/reviewer/:userId" element={<PublicReviewerProfilePage />} />
          <Route path="/gallery" element={<Navigate to="/routines" replace />} />
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/login" element={<LoginPage />} />
          {/* Legacy callback route — redirect home */}
          <Route path="/auth/callback" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <AskAFriendNudge />
    </>
  );
}

function App() {
  // Seed database on first load
  useEffect(() => {
    const seedDb = async () => {
      try {
        await axios.post(`${API}/seed`);
      } catch (error) {
        // Ignore — already seeded
      }
    };
    seedDb();
  }, []);

  return (
    <Auth0Provider
      domain={process.env.REACT_APP_AUTH0_DOMAIN}
      clientId={process.env.REACT_APP_AUTH0_CLIENT_ID}
      authorizationParams={{
        redirect_uri: window.location.origin,
      }}
      cacheLocation="localstorage"
    >
      <BrowserRouter>
        <AuthProvider>
          <div className="App min-h-screen bg-background">
            <AppRouter />
            <Toaster position="bottom-right" />
          </div>
        </AuthProvider>
      </BrowserRouter>
    </Auth0Provider>
  );
}

export default App;
